// src/admin/pages/carmodel/CarModelManagement.tsx
import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Drawer,
  FormControlLabel,
  Grid,
  IconButton,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  CircularProgress,
  Stack,
  Divider,
  Pagination,
  InputAdornment,
  Tooltip,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Backdrop,
  LinearProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Settings as SettingsIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  AddBox as AddBoxIcon,
  Search as SearchIcon,
  Delete,
  Close as CloseIcon,
} from '@mui/icons-material';
import StackedLineChartIcon from '@mui/icons-material/StackedLineChart';
import { CarModel, Trim, CarModelFormData } from './carModel';
import { useAuth } from '../../../contexts/AuthContext';
import { useCarModelsPaginated, useCarModelStats, useCarModelMutations } from '../../../hooks/useCarModels';
import { fetchActiveBrands, Brand } from '../brand/brandService';
import ToastNotification from '../common/Toastnotification';

const CAR_MODELS_PER_PAGE = 10;

interface ToastState {
  open: boolean;
  message: string;
  severity: 'success' | 'error' | 'warning' | 'info';
}

type OverlayMode = 'save-add' | 'save-edit' | 'delete' | 'toggle' | null;

const getSoftChipSx = (color: string, isDark: boolean = false) => {
  type PaletteEntry = { bg: string; border: string; text: string; iconColor: string };

  const lightPalette: Record<string, PaletteEntry> = {
    warning: { bg: 'rgba(255,167,38,0.12)', border: 'rgba(255,167,38,0.4)', text: '#b76e00', iconColor: '#e68900' },
    info: { bg: 'rgba(41,182,246,0.12)', border: 'rgba(41,182,246,0.4)', text: '#0277bd', iconColor: '#039be5' },
    primary: { bg: 'rgba(33,150,243,0.12)', border: 'rgba(33,150,243,0.4)', text: '#1565c0', iconColor: '#1976d2' },
    success: { bg: 'rgba(102,187,106,0.15)', border: 'rgba(102,187,106,0.45)', text: '#2e7d32', iconColor: '#43a047' },
    error: { bg: 'rgba(239,83,80,0.12)', border: 'rgba(239,83,80,0.4)', text: '#c62828', iconColor: '#e53935' },
    secondary: { bg: 'rgba(171,71,188,0.10)', border: 'rgba(171,71,188,0.35)', text: '#6a1b9a', iconColor: '#8e24aa' },
    default: { bg: 'rgba(158,158,158,0.12)', border: 'rgba(158,158,158,0.4)', text: '#424242', iconColor: '#757575' },
  };

  const darkPalette: Record<string, PaletteEntry> = {
    warning: { bg: 'rgba(255,167,38,0.22)', border: 'rgba(255,167,38,0.55)', text: '#ffcc80', iconColor: '#ffa726' },
    info: { bg: 'rgba(41,182,246,0.20)', border: 'rgba(41,182,246,0.50)', text: '#81d4fa', iconColor: '#29b6f6' },
    primary: { bg: 'rgba(33,150,243,0.22)', border: 'rgba(33,150,243,0.50)', text: '#90caf9', iconColor: '#42a5f5' },
    success: { bg: 'rgba(102,187,106,0.22)', border: 'rgba(102,187,106,0.50)', text: '#a5d6a7', iconColor: '#66bb6a' },
    error: { bg: 'rgba(239,83,80,0.22)', border: 'rgba(239,83,80,0.50)', text: '#ef9a9a', iconColor: '#ef5350' },
    secondary: { bg: 'rgba(171,71,188,0.22)', border: 'rgba(171,71,188,0.50)', text: '#ce93d8', iconColor: '#ab47bc' },
    default: { bg: 'rgba(158,158,158,0.22)', border: 'rgba(158,158,158,0.50)', text: '#e0e0e0', iconColor: '#bdbdbd' },
  };

  const palette = isDark ? darkPalette : lightPalette;
  const p = palette[color] ?? palette.default;

  return {
    backgroundColor: p.bg,
    borderColor: p.border,
    color: p.text,
    fontWeight: 600,
    border: `1px solid ${p.border}`,
    '& .MuiChip-icon': { color: p.iconColor },
    '& .MuiChip-label': { color: p.text },
  };
};

interface LoadingOverlayProps {
  wrapperRef: React.RefObject<HTMLDivElement | null>;
}

const LoadingOverlay: React.FC<LoadingOverlayProps> = ({ wrapperRef }) => {
  const [rect, setRect] = useState<DOMRect | null>(null);
  const theme = useTheme();

  const overlayBg = theme.palette.mode === 'dark'
    ? 'rgba(26, 29, 36, 0.85)'
    : 'rgba(255, 255, 255, 0.85)';

  React.useEffect(() => {
    const measure = () => {
      if (wrapperRef.current) setRect(wrapperRef.current.getBoundingClientRect());
    };
    measure();
    const el = wrapperRef.current;
    window.addEventListener('resize', measure);
    el?.addEventListener('scroll', measure);
    return () => {
      window.removeEventListener('resize', measure);
      el?.removeEventListener('scroll', measure);
    };
  }, [wrapperRef]);

  if (!rect) return null;

  return (
    <Box
      sx={{
        position: 'fixed',
        top: rect.top, left: rect.left,
        width: rect.width, height: rect.height,
        backgroundColor: overlayBg,
        backdropFilter: 'blur(2px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1100, pointerEvents: 'all', borderRadius: 'inherit',
      }}
    >
      <Box sx={{ textAlign: 'center' }}>
        <CircularProgress size={40} />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Loading models...
        </Typography>
      </Box>
    </Box>
  );
};

const CarModelManagement: React.FC = () => {
  const { currentUser, hasPermission, isSuperAdmin } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isDark = theme.palette.mode === 'dark';

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState<'add' | 'edit'>('add');
  const [selectedCarModel, setSelectedCarModel] = useState<CarModel | null>(null);
  const [formData, setFormData] = useState<CarModelFormData>({ name: '', brandId: '', isActive: true, trims: [] });
  const [saveLoading, setSaveLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState<string>('');
  const [toast, setToast] = useState<ToastState>({ open: false, message: '', severity: 'info' });
  const [deleteTarget, setDeleteTarget] = useState<CarModel | null>(null);
  const [deleteDialogLoading, setDeleteDialogLoading] = useState(false);
  const [brands, setBrands] = useState<Brand[]>([]);

  const [overlayMode, setOverlayMode] = useState<OverlayMode>(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const trimsContainerRef = useRef<HTMLDivElement>(null);
  const tableWrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetchActiveBrands().then(setBrands).catch(() => setBrands([]));
  }, []);

  const {
    data: carModelData,
    isLoading: carModelLoading,
    isFetching: carModelIsFetching,
  } = useCarModelsPaginated({
    page: currentPage,
    search: searchQuery,
    pageSize: CAR_MODELS_PER_PAGE,
  });

  const { data: statsData } = useCarModelStats();
  const { create, update, remove, toggle } = useCarModelMutations();

  const canView = isSuperAdmin || hasPermission('variant.view');
  const canCreate = isSuperAdmin || hasPermission('variant.create');
  const canEdit = isSuperAdmin || hasPermission('variant.edit');
  const canDelete = isSuperAdmin || hasPermission('variant.delete');
  const canToggle = isSuperAdmin || hasPermission('variant.edit');

  const carModels = carModelData?.carModels ?? [];
  const totalCarModels = carModelData?.totalCount ?? 0;
  const totalPages = Math.ceil(totalCarModels / CAR_MODELS_PER_PAGE);
  const stats = statsData ?? { total: 0, active: 0, inactive: 0, totalTrims: 0 };
  const loading = carModelLoading || carModelIsFetching;

  React.useEffect(() => {
    if (trimsContainerRef.current && formData.trims.length > 0) {
      trimsContainerRef.current.scrollTo({
        top: trimsContainerRef.current.scrollHeight,
        behavior: 'smooth',
      });
    }
  }, [formData.trims.length]);

  const showToast = (message: string, severity: 'success' | 'error' | 'warning' | 'info') => {
    setToast({ open: true, message, severity });
  };
  const handleCloseToast = () => setToast(t => ({ ...t, open: false }));

  const resetToFirstPage = () => setCurrentPage(1);

  const handlePageChange = (_event: React.ChangeEvent<unknown>, page: number) => {
    setCurrentPage(page);
  };

  const handleSearchKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      setSearchQuery(searchInput);
      resetToFirstPage();
    }
  };

  const clearSearch = () => {
    setSearchInput('');
    setSearchQuery('');
    resetToFirstPage();
    showToast('Search cleared', 'info');
  };

  const resetForm = () => {
    setFormData({ name: '', brandId: '', isActive: true, trims: [] });
    setSelectedCarModel(null);
  };

  const handleAddCarModel = () => { resetForm(); setDrawerMode('add'); setDrawerOpen(true); };

  const handleEditCarModel = (carModel: CarModel) => {
    setFormData({ name: carModel.name, brandId: carModel.brandId, isActive: carModel.isActive, trims: carModel.trims || [] });
    setSelectedCarModel(carModel);
    setDrawerMode('edit');
    setDrawerOpen(true);
  };

  const handleCloseDrawer = () => { setDrawerOpen(false); resetForm(); };

  const handleDeleteCarModel = (carModel: CarModel) => setDeleteTarget(carModel);

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    try {
      setDeleteDialogLoading(true);
      setDeleteLoading(deleteTarget.id!);
      setDeleteTarget(null);
      setOverlayMode('delete');
      const result = await remove.mutateAsync(deleteTarget.id!);
      if (result.success) {
        resetToFirstPage();
        showToast('Model deleted successfully', 'success');
      } else {
        showToast(result.message || 'Failed to delete model', 'error');
      }
    } catch {
      showToast('Failed to delete model', 'error');
    } finally {
      setDeleteDialogLoading(false);
      setDeleteLoading('');
      setOverlayMode(null);
    }
  };

  const handleDeleteCancel = () => setDeleteTarget(null);

  const handleToggleStatus = async (carModel: CarModel) => {
    try {
      setOverlayMode('toggle');
      const newStatus = !carModel.isActive;
      const result = await toggle.mutateAsync({ carModelId: carModel.id!, isActive: newStatus });
      if (result.success) {
        resetToFirstPage();
        showToast(`Model ${newStatus ? 'activated' : 'deactivated'} successfully`, 'success');
      } else {
        showToast(result.message || 'Failed to update model status', 'error');
      }
    } catch {
      showToast('Failed to update model status', 'error');
    } finally {
      setOverlayMode(null);
    }
  };

  if (!canView) {
    return (
      <Box sx={{ p: 3 }}>
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <StackedLineChartIcon sx={{ fontSize: 64, color: 'warning.main', mb: 2, opacity: 0.7 }} />
            <Typography variant="h5" gutterBottom>Access Denied</Typography>
            <Typography variant="body1" color="text.secondary">
              You don't have permission to view models. Please contact your administrator.
            </Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) { showToast('Model name is required', 'error'); return; }
    if (!formData.brandId) { showToast('A brand must be selected', 'error'); return; }
    if (formData.trims.some(t => !t.name.trim())) { showToast('All trims must have a name', 'error'); return; }

    setSaveLoading(true);
    setOverlayMode(drawerMode === 'add' ? 'save-add' : 'save-edit');
    try {
      let result;
      if (drawerMode === 'add') {
        if (!currentUser) { showToast('You must be logged in', 'error'); return; }
        result = await create.mutateAsync({ formData, userId: currentUser.uid });
        if (result.success) showToast('Model created successfully', 'success');
        else showToast(result.message || 'Failed to create model', 'error');
      } else if (selectedCarModel) {
        result = await update.mutateAsync({ carModelId: selectedCarModel.id!, formData });
        if (result.success) showToast('Model updated successfully', 'success');
        else showToast(result.message || 'Failed to update model', 'error');
      }
      if (result?.success) {
        setDrawerOpen(false);
        resetForm();
        resetToFirstPage();
      }
    } catch {
      showToast('Failed to save model', 'error');
    } finally {
      setSaveLoading(false);
      setOverlayMode(null);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleBrandChange = (e: any) => {
    setFormData(prev => ({ ...prev, brandId: e.target.value }));
  };

  const handleAddTrim = () => {
    setFormData(prev => ({ ...prev, trims: [...prev.trims, { name: '', description: '', isActive: true }] }));
  };

  const handleUpdateTrim = (index: number, field: keyof Trim, value: any) => {
    setFormData(prev => ({
      ...prev,
      trims: prev.trims.map((trim, i) => i === index ? { ...trim, [field]: value } : trim),
    }));
  };

  const handleRemoveTrim = (index: number) => {
    setFormData(prev => ({ ...prev, trims: prev.trims.filter((_, i) => i !== index) }));
  };

  const getOverlayTitle = (): string => {
    switch (overlayMode) {
      case 'save-add': return 'Creating Model…';
      case 'save-edit': return 'Updating Model…';
      case 'delete': return 'Deleting Model…';
      case 'toggle': return 'Updating Status…';
      default: return 'Please wait…';
    }
  };

  if (loading && carModels.length === 0) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" minHeight="400px">
        <CircularProgress size={40} />
        <Typography variant="body1" sx={{ ml: 2 }}>Loading models...</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: { md: '100%' },
        display: 'flex',
        flexDirection: 'column',
        overflow: { xs: 'visible', md: 'hidden' },
        p: { xs: 2, sm: 3 },
      }}
    >
      <Box sx={{ flexShrink: 0 }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          spacing={2}
          sx={{ mb: 3 }}
        >
          <Stack direction="row" alignItems="center" spacing={2}>
            <StackedLineChartIcon sx={{ fontSize: { xs: 28, md: 32 }, color: 'primary.main' }} />
            <Box>
              <Typography variant="h4" component="h1" sx={{ fontSize: { xs: '1.5rem', md: '2rem' } }}>
                Models & Trims
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Manage vehicle models per brand and their trim/spec levels
              </Typography>
            </Box>
          </Stack>
          {canCreate && (
            <Button
              onClick={handleAddCarModel}
              variant="contained"
              startIcon={<AddIcon />}
              sx={{ alignSelf: { xs: 'flex-start', sm: 'auto' } }}
            >
              Add Model
            </Button>
          )}
        </Stack>

        {/* ── Stats Cards ── */}
        <Grid container spacing={{ xs: 1.5, sm: 2, md: 3 }} sx={{ mb: 3 }} alignItems="stretch">
          <Grid item xs={6} sm={6} md={3} sx={{ display: 'flex' }}>
            <Card sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } }, flex: 1, display: 'flex', flexDirection: 'column' }}>
                <Typography color="text.secondary" gutterBottom variant="body2" noWrap>Models</Typography>
                <Typography variant="h5" component="div" color="text.primary"
                  sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem', md: '2rem' }, mb: 1 }}>
                  {stats.total}
                </Typography>
                <Box sx={{ mt: 'auto', display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'success.main', flexShrink: 0 }} />
                    <Typography variant="caption" color="success.main" noWrap>{stats.active} Active</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'text.disabled', flexShrink: 0 }} />
                    <Typography variant="caption" color="text.secondary" noWrap>{stats.inactive} Inactive</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={6} sm={6} md={3} sx={{ display: 'flex' }}>
            <Card sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } }, flex: 1, display: 'flex', flexDirection: 'column' }}>
                <Typography color="text.secondary" gutterBottom variant="body2" noWrap>Total Trims</Typography>
                <Typography variant="h5" component="div" color="text.primary"
                  sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem', md: '2rem' }, mb: 1 }}>
                  {stats.totalTrims}
                </Typography>
                <Box sx={{ mt: 'auto' }}>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {stats.total > 0 ? `avg ${(stats.totalTrims / stats.total).toFixed(1)} per model` : '—'}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={6} sm={6} md={3} sx={{ display: 'flex' }}>
            <Card sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } }, flex: 1, display: 'flex', flexDirection: 'column' }}>
                <Typography color="text.secondary" gutterBottom variant="body2" noWrap>Active Models</Typography>
                <Typography variant="h5" component="div" color="text.primary"
                  sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem', md: '2rem' }, mb: 1 }}>
                  {stats.active}
                </Typography>
                <Box sx={{ mt: 'auto' }}>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {stats.total > 0 ? `${((stats.active / stats.total) * 100).toFixed(1)}% of total` : '—'}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={6} sm={6} md={3} sx={{ display: 'flex' }}>
            <Card sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } }, flex: 1, display: 'flex', flexDirection: 'column' }}>
                <Typography color="text.secondary" gutterBottom variant="body2" noWrap>Inactive Models</Typography>
                <Typography variant="h5" component="div" color="text.primary"
                  sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem', md: '2rem' }, mb: 1 }}>
                  {stats.inactive}
                </Typography>
                <Box sx={{ mt: 'auto' }}>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {stats.total > 0 ? `${((stats.inactive / stats.total) * 100).toFixed(1)}% suspended` : '—'}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* ── Search ── */}
        <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: 2 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}
            alignItems={{ xs: 'stretch', sm: 'center' }} justifyContent="space-between">
            <TextField
              placeholder="Search by model name, brand, trims... (Press Enter)"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyPress={handleSearchKeyPress}
              sx={{ flexGrow: 1, maxWidth: { sm: 500 } }}
              size="small"
              InputProps={{
                startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
              }}
            />
            {searchQuery && (
              <Button onClick={clearSearch} variant="outlined" size="small" startIcon={<Delete />}>
                Clear Search
              </Button>
            )}
          </Stack>
          {searchQuery && (
            <Box sx={{ mt: 1.5 }}>
              <Typography variant="body2" color="text.secondary">
                Found {carModels.length} models matching "{searchQuery}"
              </Typography>
            </Box>
          )}
        </Paper>
      </Box>

      {/* ── Scrollable Body ── */}
      <Box sx={{ flex: 1, overflow: { xs: 'visible', md: 'auto' }, display: 'flex', flexDirection: 'column', minHeight: { xs: 'auto', md: 0 } }}>

        {carModels.length === 0 && !loading && (
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
            <Card sx={{ width: '100%', maxWidth: 500 }}>
              <CardContent sx={{ textAlign: 'center', py: 6 }}>
                <SettingsIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2, opacity: 0.5 }} />
                <Typography variant="h5" color="text.secondary" gutterBottom>
                  {searchQuery ? 'No Models Match Your Search' : 'No Models Found'}
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 400, mx: 'auto' }}>
                  {searchQuery
                    ? 'Try adjusting your search query.'
                    : "You haven't added any models yet. Start by adding a model (e.g. Corolla) under a brand, then list its trims (e.g. GLi, Altis)."}
                </Typography>
                {searchQuery ? (
                  <Button onClick={clearSearch} variant="outlined">Clear Search</Button>
                ) : canCreate && (
                  <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddCarModel}>
                    Add Model
                  </Button>
                )}
              </CardContent>
            </Card>
          </Box>
        )}

        {carModels.length > 0 && (
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: { xs: 'auto', md: 0 } }}>
            <TableContainer
              component={Paper}
              ref={tableWrapperRef}
              sx={{
                flex: 1,
                overflow: { xs: 'auto', md: 'auto' },
                maxHeight: { md: '100%' },
                mb: 2,
                position: 'relative',
                '& .MuiTable-root': { minWidth: { xs: 650, md: 800 } },
              }}
            >
              <Table stickyHeader size={isMobile ? 'small' : 'medium'}>
                <TableHead>
                  <TableRow>
                    <TableCell>Model</TableCell>
                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Brand</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Trims</TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Created At</TableCell>
                    {(canEdit || canToggle || canDelete) && (
                      <TableCell align="right">Actions</TableCell>
                    )}                  </TableRow>
                </TableHead>
                <TableBody>
                  {carModels.map((carModel) => (
                    <TableRow
                      key={carModel.id}
                      hover
                      sx={{
                        '&:hover': { backgroundColor: 'action.hover' },
                        '&:last-child td, &:last-child th': { borderBottom: 0 },
                      }}
                    >
                      <TableCell>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: { xs: '0.85rem', sm: '1rem' } }}>
                          {carModel.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: { xs: 'block', sm: 'none' } }}>
                          {carModel.brandName} · {carModel.trims?.length || 0} trim{(carModel.trims?.length || 0) !== 1 ? 's' : ''}
                        </Typography>
                      </TableCell>

                      <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                        <Typography variant="body2">{carModel.brandName || '—'}</Typography>
                      </TableCell>

                      <TableCell>
                        <Chip
                          label={carModel.isActive ? 'Active' : 'Inactive'}
                          size="small"
                          variant="outlined"
                          sx={getSoftChipSx(carModel.isActive ? 'success' : 'default', isDark)}
                        />
                      </TableCell>

                      <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                        <Box sx={{ maxWidth: 300 }}>
                          {carModel.trims && carModel.trims.length > 0 ? (
                            <Stack direction="row" spacing={0.5} flexWrap="wrap" gap={0.5}>
                              {carModel.trims.slice(0, 5).map((trim, index) => (
                                <Chip
                                  key={trim.id || index}
                                  label={trim.name}
                                  size="small"
                                  variant="outlined"
                                  sx={getSoftChipSx(trim.isActive ? 'default' : 'error', isDark)}
                                />
                              ))}
                              {carModel.trims.length > 5 && (
                                <Chip
                                  label={`+${carModel.trims.length - 5} more`}
                                  size="small"
                                  variant="outlined"
                                  sx={getSoftChipSx('primary', isDark)}
                                />
                              )}
                            </Stack>
                          ) : (
                            <Typography variant="body2" color="text.secondary">No trims</Typography>
                          )}
                        </Box>
                      </TableCell>

                      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                        <Typography variant="body2" color="text.secondary">
                          {carModel.createdAt
                            ? new Intl.DateTimeFormat('en-US', { year: 'numeric', month: 'short', day: 'numeric' }).format(carModel.createdAt)
                            : '-'}
                        </Typography>
                      </TableCell>

                      {(canEdit || canToggle || canDelete) && (
                        <TableCell align="right">
                          <Stack direction="row" spacing={0.5} justifyContent="flex-end" flexWrap="nowrap">
                            {canEdit && (
                              <Tooltip title="Edit">
                                <IconButton onClick={() => handleEditCarModel(carModel)} size="small" color="primary">
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            {canToggle && (
                              <Tooltip title={carModel.isActive ? 'Deactivate' : 'Activate'}>
                                <IconButton onClick={() => handleToggleStatus(carModel)} size="small" color="info">
                                  {carModel.isActive ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                                </IconButton>
                              </Tooltip>
                            )}
                            {canDelete && (
                              <Tooltip title="Delete">
                                <IconButton
                                  onClick={() => handleDeleteCarModel(carModel)}
                                  disabled={deleteLoading === carModel.id}
                                  size="small" color="error"
                                >
                                  {deleteLoading === carModel.id
                                    ? <CircularProgress size={16} />
                                    : <DeleteIcon fontSize="small" />}
                                </IconButton>
                              </Tooltip>
                            )}
                          </Stack>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {loading && <LoadingOverlay wrapperRef={tableWrapperRef} />}

            <Paper elevation={2} sx={{ flexShrink: 0, borderRadius: 1 }}>
              <Box sx={{ py: 2, px: 2 }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 1.5, sm: 3 }}
                  alignItems="center" justifyContent="space-between" sx={{ width: '100%' }}>
                  <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                    Showing {totalCarModels === 0 ? 0 : (currentPage - 1) * CAR_MODELS_PER_PAGE + 1}–
                    {Math.min(currentPage * CAR_MODELS_PER_PAGE, totalCarModels)} of {totalCarModels} models
                  </Typography>
                  <Pagination
                    count={Math.max(1, totalPages)} page={currentPage} onChange={handlePageChange}
                    color="primary" size={isMobile ? 'small' : 'medium'} showFirstButton showLastButton
                    disabled={loading || totalPages <= 1}
                  />
                  <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                    Page {currentPage} of {Math.max(1, totalPages)}
                  </Typography>
                </Stack>
              </Box>
            </Paper>
          </Box>
        )}
      </Box>

      {/* ── Drawer ── */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={handleCloseDrawer}
        PaperProps={{ sx: { width: { xs: '100%', sm: 560 } } }}
      >
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          <Box sx={{
            px: 3, py: 2, borderBottom: 1, borderColor: 'divider',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0,
          }}>
            <Typography variant="h6">
              {drawerMode === 'add' ? 'Add New Model' : 'Edit Model'}
            </Typography>
            <IconButton onClick={handleCloseDrawer} edge="end"><CloseIcon /></IconButton>
          </Box>

          <Box component="form" onSubmit={handleSubmit} sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0 }}>
            <Box sx={{ px: 3, pt: 3, flexShrink: 0 }}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <FormControl fullWidth required>
                    <InputLabel>Brand</InputLabel>
                    <Select
                      name="brandId"
                      value={formData.brandId}
                      onChange={handleBrandChange}
                      label="Brand"
                    >
                      <MenuItem value="">
                        <em>Select Brand</em>
                      </MenuItem>
                      {brands.map((brand) => (
                        <MenuItem key={brand.id} value={brand.id}>
                          {brand.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth label="Model Name" name="name"
                    value={formData.name} onChange={handleInputChange}
                    required placeholder="e.g., Corolla, Civic, CX-5"
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={<Switch name="isActive" checked={formData.isActive} onChange={handleInputChange} />}
                    label="Active"
                  />
                </Grid>
              </Grid>

              <Divider sx={{ mt: 3, mb: 0 }} />

              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 2.5, mb: 1.5 }}>
                <Typography variant="subtitle1" fontWeight={600}>Trims</Typography>
                <Button onClick={handleAddTrim} startIcon={<AddBoxIcon />} variant="outlined" size="small">
                  Add Trim
                </Button>
              </Stack>
            </Box>

            <Box
              ref={trimsContainerRef}
              sx={{
                flex: 1, overflow: 'auto', px: 3, pb: 3, minHeight: 0,
                '&::-webkit-scrollbar': { width: '6px' },
                '&::-webkit-scrollbar-track': { background: 'transparent' },
                '&::-webkit-scrollbar-thumb': { background: 'rgba(0,0,0,0.2)', borderRadius: '3px' },
                '&::-webkit-scrollbar-thumb:hover': { background: 'rgba(0,0,0,0.35)' },
              }}
            >
              {formData.trims.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4, border: '2px dashed', borderColor: 'divider', borderRadius: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    No trims added yet. Click "Add Trim" to get started (e.g. GLi, Altis, Turbo).
                  </Typography>
                </Box>
              ) : (
                <Stack spacing={1.5}>
                  {formData.trims.map((trim, index) => (
                    <Paper key={index} variant="outlined" sx={{ p: 2, borderRadius: 1 }}>
                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} sm={4}>
                          <TextField
                            fullWidth label="Trim Name" value={trim.name}
                            onChange={(e) => handleUpdateTrim(index, 'name', e.target.value)}
                            placeholder="e.g., GLi, Altis" size="small"
                            autoFocus={index === formData.trims.length - 1}
                          />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <TextField
                            fullWidth label="Description (Optional)" value={trim.description || ''}
                            onChange={(e) => handleUpdateTrim(index, 'description', e.target.value)}
                            placeholder="e.g., 1.8L, CVT" size="small"
                          />
                        </Grid>
                        <Grid item xs={8} sm={2}>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={trim.isActive}
                                onChange={(e) => handleUpdateTrim(index, 'isActive', e.target.checked)}
                                size="small"
                              />
                            }
                            label="Active"
                            sx={{ mr: 0 }}
                          />
                        </Grid>
                        <Grid item xs={4} sm={2} sx={{ display: 'flex', justifyContent: { xs: 'flex-end', sm: 'center' } }}>
                          <Tooltip title="Remove Trim">
                            <IconButton onClick={() => handleRemoveTrim(index)} color="error" size="small">
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Grid>
                      </Grid>
                    </Paper>
                  ))}
                </Stack>
              )}
            </Box>

            <Divider />
            <Box sx={{ px: 3, py: 2, display: 'flex', gap: 2, justifyContent: 'flex-end', flexShrink: 0 }}>
              <Button onClick={handleCloseDrawer} disabled={saveLoading} startIcon={<CancelIcon />}>Cancel</Button>
              <Button type="submit" variant="contained" disabled={saveLoading}
                startIcon={saveLoading ? <CircularProgress size={16} /> : <SaveIcon />}>
                {drawerMode === 'add' ? 'Create Model' : 'Update Model'}
              </Button>
            </Box>
          </Box>
        </Box>
      </Drawer>

      {/* ── Delete Confirmation Dialog ── */}
      <Dialog open={!!deleteTarget} onClose={handleDeleteCancel} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Model</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete{' '}
            <strong>"{deleteTarget?.name}"</strong>?{' '}
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleDeleteCancel} disabled={deleteDialogLoading}>Cancel</Button>
          <Button
            variant="contained" color="error" onClick={handleDeleteConfirm}
            disabled={deleteDialogLoading}
            startIcon={deleteDialogLoading ? <CircularProgress size={16} /> : <DeleteIcon />}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Full-screen operation overlay ── */}
      <Backdrop
        open={overlayMode !== null}
        sx={{ zIndex: (t) => t.zIndex.drawer + 200, flexDirection: 'column', gap: 3, bgcolor: 'rgba(0,0,0,0.6)' }}
      >
        <Box
          sx={{
            bgcolor: 'background.paper',
            borderRadius: 3,
            px: 5,
            py: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2.5,
            minWidth: 280,
            boxShadow: 24,
          }}
        >
          <CircularProgress size={48} thickness={4} />
          <Box sx={{ width: '100%' }}>
            <LinearProgress sx={{ borderRadius: 1, mb: 1.5 }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 600, textAlign: 'center', color: 'text.primary' }}>
              {getOverlayTitle()}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 0.5 }}>
              Please wait, do not close this window
            </Typography>
          </Box>
        </Box>
      </Backdrop>

      <ToastNotification open={toast.open} message={toast.message} severity={toast.severity} onClose={handleCloseToast} />
    </Box>
  );
};

export default CarModelManagement;
